import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";

const CONTACT_EMAIL = "info@2ra-edition.com";
const LAST_UPDATED = "28 August 2026";

const COPY = {
  en: {
    title: "Terms of Service",
    updated: "Last updated:",
    intro:
      "These Terms of Service (\"Terms\") govern your access to and use of The Archive, a digital library operated by 2RA-Edition (\"we\", \"us\", \"our\"). By creating an account or using the service you agree to these Terms. If you do not agree, please do not use the service.",

    s1_h: "1. The service",
    s1_p:
      "The Archive is an online library of books, articles, theses, and technical documents, primarily in the fields of engineering, extractive metallurgy, mining, chemistry, and applied scientific research. The service provides full-text search, AI-generated metadata and abstracts, translation, evidence-grounded research drafting, downloads, and — for paying subscribers — unlimited access to the entire catalog.",

    s2_h: "2. Accounts",
    s2_p:
      "You must be at least 16 years old to create an account. You are responsible for keeping your credentials confidential and for all activity that occurs under your account. Please notify us immediately at ",
    s2_p_end:
      " if you suspect unauthorized access. We may suspend or terminate accounts that violate these Terms, that engage in fraud, or that abuse the service (for example, automated scraping, credential sharing, or attempts to bypass download limits).",

    s3_h: "3. Free trial, subscriptions, and payments",
    s3_p:
      "New accounts include a 5-day free trial with unlimited downloads. After the trial, you can continue on the free tier (10 downloads per month) or subscribe to a paid plan (1, 3, 6, or 12 months). Paid subscriptions are billed in advance via Stripe and are non-refundable except where required by applicable law. You can cancel at any time from your profile page — access continues until the end of the current paid period.",

    s4_h: "4. Content and licensing",
    s4_p1:
      "All documents on The Archive remain the property of their respective rights holders. Access via your subscription grants you a personal, non-exclusive, non-transferable licence to read, download, and cite documents for your own research, study, or professional work. You may not:",
    s4_l1: "redistribute, resell, or republish documents obtained from the service;",
    s4_l2: "share your login credentials or account with any third party;",
    s4_l3:
      "use automated tools to bulk-download, scrape, or mirror the catalog without our written consent;",
    s4_l4:
      "remove copyright notices, watermarks, or metadata from downloaded files.",

    s5_h: "5. Submitting documents",
    s5_p:
      "Users may submit their own documents through the submission portal. By submitting a document you warrant that you own the rights or have permission to publish it, and you grant 2RA-Edition a non-exclusive licence to host, index, translate abstracts of, and distribute the document via the service. Approved submissions may earn a share of the paid download revenue, subject to the platform's revenue-sharing policy in effect at the time and payout via Stripe Connect. We reserve the right to decline, remove, or unpublish submissions at our discretion — for example if they duplicate existing content, breach a third party's rights, or fall outside the library's scope.",

    s6_h: "6. Acceptable use",
    s6_p:
      "You agree not to use the service to (a) infringe the intellectual property or privacy rights of others; (b) upload malware, illegal content, or content that violates the rights of a third party; (c) attempt to gain unauthorised access to the service, other user accounts, or its underlying infrastructure; or (d) resell any portion of the service.",

    s7_h: "7. Privacy",
    s7_p:
      "We collect only the data required to operate the service: your email, name, hashed password, subscription and download history, IP-derived country (used for aggregate statistics), and audit logs of administrative actions. We do not sell your data. We use Resend for transactional email delivery, Stripe for payment processing, and standard AI providers (OpenAI, Anthropic, Google) for abstract generation, translation, document questions, and evidence-grounded research drafting. When you use an AI feature, your request and the relevant document text or excerpts are sent to the configured AI provider to generate the response. Full documents remain stored on our own infrastructure and are accessible through an authenticated session.",

    s8_h: "8. Refunds",
    s8_p:
      "Subscription payments are generally non-refundable. If you experience a service failure or believe you were charged in error, please contact ",
    s8_p_end:
      " within 14 days of the charge and we will review the case in good faith.",

    s9_h: "9. Disclaimers and liability",
    s9_p:
      "The Archive is provided \"as is\". While we work hard to maintain accurate metadata, correct OCR, and reliable uptime, we do not warrant that the service will be uninterrupted or error-free, or that all documents are free of errors or omissions. To the maximum extent permitted by law, 2RA-Edition is not liable for indirect or consequential damages arising from your use of the service. Nothing in these Terms excludes any liability that cannot be excluded by law.",

    s10_h: "10. Changes to these Terms",
    s10_p:
      "We may update these Terms from time to time. Material changes will be announced by email or via an in-service notice at least 14 days before they take effect. Continued use of the service after the effective date constitutes acceptance of the revised Terms.",

    s11_h: "11. Governing law and contact",
    s11_p:
      "These Terms are governed by the laws of the Democratic Republic of the Congo, without regard to conflict-of-law principles. Any dispute arising out of or relating to these Terms shall be resolved before the competent courts of Lubumbashi, DRC, save where mandatory consumer law provides otherwise. Questions? Write to ",
    s11_p_end: ".",

    back: "Back",
  },
  fr: {
    title: "Conditions d'utilisation",
    updated: "Dernière mise à jour :",
    intro:
      "Les présentes Conditions d'utilisation (« Conditions ») encadrent votre accès aux Archives et votre utilisation du service, exploité par 2RA-Edition (« nous », « notre »). La création d'un compte ou l'utilisation du service vaut acceptation des présentes Conditions. En cas de désaccord, veuillez ne pas utiliser le service.",

    s1_h: "1. Le service",
    s1_p:
      "Les Archives est une bibliothèque en ligne de livres, articles, thèses et documents techniques, principalement dans les domaines de l'ingénierie, de la métallurgie extractive, des mines, de la chimie et de la recherche scientifique appliquée. Le service propose la recherche plein-texte, des métadonnées et résumés générés par IA, la traduction, la rédaction de recherche fondée sur des sources, le téléchargement, et — pour les abonnés payants — un accès illimité à l'ensemble du catalogue.",

    s2_h: "2. Comptes",
    s2_p:
      "Vous devez être âgé d'au moins 16 ans pour créer un compte. Vous êtes responsable de la confidentialité de vos identifiants et de toute activité effectuée sous votre compte. Merci de nous prévenir sans délai à ",
    s2_p_end:
      " en cas de suspicion d'accès non autorisé. Nous pouvons suspendre ou résilier tout compte qui enfreint ces Conditions, se livre à des fraudes ou abuse du service (par exemple par du scraping automatisé, du partage d'identifiants, ou des tentatives de contournement des limites de téléchargement).",

    s3_h: "3. Essai gratuit, abonnements et paiements",
    s3_p:
      "Chaque nouveau compte bénéficie d'un essai gratuit de 5 jours avec téléchargements illimités. À l'issue de l'essai, vous pouvez rester sur le palier gratuit (10 téléchargements par mois) ou souscrire à un abonnement payant (1, 3, 6 ou 12 mois). Les abonnements sont facturés d'avance via Stripe et ne sont pas remboursables sauf lorsque la loi l'exige. Vous pouvez résilier à tout moment depuis votre profil — l'accès se poursuit jusqu'au terme de la période payée en cours.",

    s4_h: "4. Contenus et licence",
    s4_p1:
      "Tous les documents restent la propriété de leurs ayants droit respectifs. Votre abonnement vous accorde une licence personnelle, non exclusive et non transférable pour lire, télécharger et citer les documents dans le cadre de votre recherche, de vos études ou de votre activité professionnelle. Vous ne pouvez pas :",
    s4_l1: "redistribuer, revendre ou republier des documents obtenus via le service ;",
    s4_l2: "partager vos identifiants ou votre compte avec un tiers ;",
    s4_l3:
      "utiliser des outils automatisés pour télécharger en masse, aspirer ou dupliquer le catalogue sans notre accord écrit ;",
    s4_l4:
      "retirer les mentions de droit d'auteur, filigranes ou métadonnées des fichiers téléchargés.",

    s5_h: "5. Soumission de documents",
    s5_p:
      "Vous pouvez soumettre vos propres documents via le portail de soumission. En soumettant un document, vous garantissez en détenir les droits ou disposer des autorisations nécessaires, et vous accordez à 2RA-Edition une licence non exclusive pour l'héberger, l'indexer, en traduire le résumé et le distribuer via le service. Les soumissions acceptées peuvent percevoir une part des revenus des téléchargements payants, selon la politique de partage en vigueur, réglée via Stripe Connect. Nous nous réservons le droit de refuser, retirer ou dépublier une soumission à notre discrétion — par exemple si elle duplique un contenu existant, porte atteinte aux droits d'un tiers, ou sort du périmètre de la bibliothèque.",

    s6_h: "6. Usage acceptable",
    s6_p:
      "Vous vous engagez à ne pas utiliser le service pour (a) porter atteinte aux droits de propriété intellectuelle ou à la vie privée de tiers ; (b) déposer un contenu malveillant, illégal, ou violant les droits d'un tiers ; (c) tenter d'accéder sans autorisation au service, aux comptes d'autres utilisateurs ou à son infrastructure ; ou (d) revendre tout ou partie du service.",

    s7_h: "7. Vie privée",
    s7_p:
      "Nous ne collectons que les données nécessaires au fonctionnement du service : email, nom, mot de passe haché, historique d'abonnement et de téléchargement, pays déduit de l'adresse IP (utilisé à des fins statistiques agrégées) et journaux d'audit des actions administratives. Nous ne vendons pas vos données. Nous utilisons Resend pour l'envoi des emails transactionnels, Stripe pour le paiement, et des fournisseurs d'IA standards (OpenAI, Anthropic, Google) pour les résumés, la traduction, les questions sur les documents et la rédaction fondée sur des sources. Lorsque vous utilisez une fonction d'IA, votre demande ainsi que le texte ou les extraits pertinents sont transmis au fournisseur configuré afin de générer la réponse. Les documents complets restent hébergés sur notre propre infrastructure et sont accessibles via une session authentifiée.",

    s8_h: "8. Remboursements",
    s8_p:
      "Les paiements d'abonnement sont en général non remboursables. En cas de panne de service ou de facturation erronée, veuillez écrire à ",
    s8_p_end:
      " dans les 14 jours suivant le prélèvement ; nous étudierons la demande de bonne foi.",

    s9_h: "9. Garanties et responsabilité",
    s9_p:
      "Les Archives est fourni « en l'état ». Bien que nous nous efforcions de maintenir des métadonnées précises, une OCR correcte et une disponibilité fiable, nous ne garantissons pas que le service sera ininterrompu ou exempt d'erreurs, ni que tous les documents sont exempts d'inexactitudes ou d'omissions. Dans la limite maximale permise par la loi, 2RA-Edition ne saurait être tenue pour responsable de dommages indirects ou consécutifs découlant de l'usage du service. Rien dans ces Conditions n'exclut la responsabilité qui ne peut être exclue par la loi.",

    s10_h: "10. Modifications des présentes Conditions",
    s10_p:
      "Nous pouvons modifier ces Conditions ponctuellement. Les changements importants seront annoncés par email ou par notice interne au moins 14 jours avant leur entrée en vigueur. L'utilisation continue du service après cette date vaut acceptation des Conditions révisées.",

    s11_h: "11. Loi applicable et contact",
    s11_p:
      "Ces Conditions sont régies par le droit de la République Démocratique du Congo, à l'exclusion des règles de conflit de lois. Tout litige né des présentes ou lié à celles-ci relève des tribunaux compétents de Lubumbashi (RDC), sauf disposition impérative contraire du droit de la consommation. Une question ? Écrivez à ",
    s11_p_end: ".",

    back: "Retour",
  },
};

const SERIF = { fontFamily: "'Fraunces', 'Cormorant Garamond', serif" };

const Section = ({ heading, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-900 mb-3" style={SERIF}>
      {heading}
    </h2>
    <div className="text-slate-700 leading-relaxed text-[15px] space-y-3">
      {children}
    </div>
  </section>
);

const EmailLink = () => (
  <a
    href={`mailto:${CONTACT_EMAIL}`}
    className="text-orange-600 hover:underline font-medium"
  >
    {CONTACT_EMAIL}
  </a>
);

const TermsPage = ({ user }) => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(
    () => localStorage.getItem("archive_lang") || "en",
  );
  const t = COPY[lang];

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 mb-8 inline-flex items-center gap-1"
          data-testid="terms-back-btn"
        >
          ← {t.back}
        </button>

        <div className="flex gap-2 mb-6 text-xs" data-testid="terms-lang-toggle">
          <button
            onClick={() => {
              setLang("en");
              localStorage.setItem("archive_lang", "en");
            }}
            className={`uppercase tracking-widest ${lang === "en" ? "text-slate-900 font-semibold" : "text-slate-400 hover:text-slate-700"}`}
          >
            EN
          </button>
          <span className="text-slate-300">/</span>
          <button
            onClick={() => {
              setLang("fr");
              localStorage.setItem("archive_lang", "fr");
            }}
            className={`uppercase tracking-widest ${lang === "fr" ? "text-slate-900 font-semibold" : "text-slate-400 hover:text-slate-700"}`}
          >
            FR
          </button>
        </div>

        <h1
          className="text-4xl md:text-5xl font-semibold text-slate-900 mb-3 tracking-tight"
          style={SERIF}
          data-testid="terms-title"
        >
          {t.title}
        </h1>
        <p className="text-sm text-slate-500 mb-10">
          {t.updated} {LAST_UPDATED}
        </p>

        <p className="text-slate-700 leading-relaxed mb-10 text-[15px]">
          {t.intro}
        </p>

        <Section heading={t.s1_h}>
          <p>{t.s1_p}</p>
        </Section>

        <Section heading={t.s2_h}>
          <p>
            {t.s2_p}
            <EmailLink />
            {t.s2_p_end}
          </p>
        </Section>

        <Section heading={t.s3_h}>
          <p>{t.s3_p}</p>
        </Section>

        <Section heading={t.s4_h}>
          <p>{t.s4_p1}</p>
          <ul className="list-disc list-inside space-y-2">
            <li>{t.s4_l1}</li>
            <li>{t.s4_l2}</li>
            <li>{t.s4_l3}</li>
            <li>{t.s4_l4}</li>
          </ul>
        </Section>

        <Section heading={t.s5_h}>
          <p>{t.s5_p}</p>
        </Section>

        <Section heading={t.s6_h}>
          <p>{t.s6_p}</p>
        </Section>

        <Section heading={t.s7_h}>
          <p>{t.s7_p}</p>
        </Section>

        <Section heading={t.s8_h}>
          <p>
            {t.s8_p}
            <EmailLink />
            {t.s8_p_end}
          </p>
        </Section>

        <Section heading={t.s9_h}>
          <p>{t.s9_p}</p>
        </Section>

        <Section heading={t.s10_h}>
          <p>{t.s10_p}</p>
        </Section>

        <Section heading={t.s11_h}>
          <p>
            {t.s11_p}
            <EmailLink />
            {t.s11_p_end}
          </p>
        </Section>
      </div>
    </div>
  );
};

export default TermsPage;
